<script setup>
import {usePortalContentAndDesignStore} from "../../../../../../stores/partners/portalContentAndDesignStore";
import EditableTextField from "../../../../portal-editable-fields/EditableTextField.vue";
import EditableResourceTextEditorField
    from "../../../../portal-editable-fields/resources/EditableResourceTextEditorField.vue";
import EditableResourceImageField
    from "../../../../portal-editable-fields/resources/EditableResourceImageField.vue";
import EditableResourceButtonField
    from "../../../../portal-editable-fields/resources/EditableResourceButtonField.vue";
import {ref, computed} from "vue";
import { useHoverTracker} from "../../../../../../composables/partners/useHoverTracker";

const { hoverContentActive, isHoverContentActive } = useHoverTracker()

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
});

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const hoverMainContentImageContainer = ref(false);

const alignmentClass = computed(() => {
    switch (props.resource.alignment) {
        case 'center': return 'justify-center text-center';
        case 'right': return 'justify-end text-right';
        default: return 'justify-start text-left';
    }
});

const headerLayoutClass = computed(() => {
    const position = props.resource.main_image_position || 'left';
    const alignment = props.resource.alignment || 'left';

    if (position === 'top') {
        if (alignment === 'center') return 'flex flex-col items-center gap-4';
        if (alignment === 'right') return 'flex flex-col items-end gap-4';
        return 'flex flex-col items-start gap-4';
    }

    if (alignment === 'center') return 'flex flex-col items-center gap-4';
    return 'flex items-center gap-6';
});

const imageSizeClass = computed(() => {
    // Backwards compatibility: check for old retina resize setting
    if (props.resource.main_image_size === undefined && props.resource.main_image_retina_resize !== undefined) {
        return props.resource.main_image_retina_resize ? 'max-w-8' : 'max-w-16';
    }
    // New size setting
    const size = props.resource.main_image_size || 'medium';
    switch (size) {
        case 'small':
        case '@2x': return 'max-w-8';
        case 'large': return 'max-w-24';
        default: return 'max-w-16'; // medium
    }
});

const imageWidthClass = (block) => {
    const w = block.image_width || '1/2';
    const map = { '1/2': 'sm:w-1/2', '1/3': 'sm:w-1/3', '2/5': 'sm:w-2/5' };
    return map[w] || 'sm:w-1/2';
};

const imageClass = (block) => {
    const border = (block.image_border ?? true) ? 'border' : '';
    return `rounded ${border}`.trim();
};

</script>

<template>
    <div>
        <div :class="[headerLayoutClass, alignmentClass]"
             @mouseenter="hoverMainContentImageContainer = true"
             @mouseleave="hoverMainContentImageContainer = false"
             :id="resource.unique_id"
        >
            <editable-resource-image-field
                resource-image-path="main_image"
                :resource-content="resource"
                :img-class="imageSizeClass"
                img-name="main_image"
                :show-add-image-button="hoverMainContentImageContainer"
            />

            <div class="space-y-1.5">
                <editable-text-field
                    v-if="resource.title"
                    text-type="h3"
                    :class="['text-xl md:text-2xl font-semibold', portalContentAndDesignStore.getPageContentTextColorClass]"
                    v-model="resource.title"
                />
                <editable-resource-text-editor-field
                    v-if="resource.description"
                    :class="[
                        'partnero-page-content-section text-base md:text-lg text-opacity-80 space-y-1',
                        portalContentAndDesignStore.getPageContentTextColorClass
                    ]"
                    :resource="resource"
                    resource-field="description"
                />
            </div>
        </div>

        <div
            :class="[
                (resource.title || resource.description) ? 'mt-4 md:mt-6' : '',
                'grid gap-6',
                resource.display_blocks_in_columns
                  ? `md:gap-12 md:grid-cols-${Math.min(resource.columns_count || 3, resource.contents?.length || 1)}`
                  : 'md:gap-8'
              ]"
        >
            <div
                v-for="content in resource.contents"
                :key="content.unique_id"
                :id="content.unique_id"
                :class="(content.image_position === 'left' || content.image_position === 'right')
                    ? `flex flex-col sm:flex-row gap-6 ${content.image_position === 'right' ? 'sm:flex-row-reverse' : ''}`
                    : 'flex flex-col items-start space-y-4'"
                @mouseenter="hoverContentActive(content.unique_id, true)"
                @mouseleave="hoverContentActive(content.unique_id, false)"
            >
                <div v-if="(content.image_position === 'left' || content.image_position === 'right') && content.files?.content_block_image?.url"
                    :class="[imageWidthClass(content), 'flex-shrink-0']"
                >
                    <editable-resource-image-field
                        :resource-content="content"
                        resource-image-path="files.content_block_image"
                        :img-class="imageClass(content)"
                        img-name="content_block_image"
                        :show-add-image-button="isHoverContentActive(content.unique_id)"
                    />
                </div>

                <div :class="(content.image_position === 'left' || content.image_position === 'right') ? 'flex-1 space-y-2' : 'space-y-2'">
                    <editable-text-field
                        v-if="content.title"
                        text-type="custom"
                        class="text-md md:text-lg font-semibold"
                        v-model="content.title"
                    />
                    <editable-resource-image-field
                        v-if="content.files?.content_block_image?.url && (!content.image_position || content.image_position === 'top')"
                        :resource-content="content"
                        resource-image-path="files.content_block_image"
                        :img-class="imageClass(content)"
                        img-name="content_block_image"
                        :show-add-image-button="isHoverContentActive(content.unique_id)"
                    />
                    <editable-resource-text-editor-field
                        v-if="content.text"
                        class="partnero-page-content-block text-sm md:text-base space-y-3"
                        :class="portalContentAndDesignStore.getPageContentTextColorClass"
                        :resource="content"
                        resource-field="text"
                    />
                    <editable-resource-button-field
                        v-if="content.button"
                        :resource-content="content"
                        :class="[
                            'w-full sm:w-auto !mt-2',
                            portalContentAndDesignStore.getPublicPageButtonCorners
                        ]"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
